import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, InputAdornment,
  IconButton, Alert, CircularProgress, Divider, Chip,
} from '@mui/material';
import {
  EmailOutlined, LockOutlined, Visibility, VisibilityOff,
  AdminPanelSettingsOutlined, AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { alpha } from '@mui/material/styles';

const PRIMARY = '#1075BA';

const BlobBg = () => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
    <Box sx={{
      position: 'absolute', width: 600, height: 600, top: -180, right: -160,
      background: `radial-gradient(circle, ${alpha(PRIMARY, 0.18)} 0%, transparent 70%)`,
      borderRadius: '50%',
      animation: 'blobFloat1 8s ease-in-out infinite',
      '@keyframes blobFloat1': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(-30px, 30px) scale(1.05)' },
      },
    }} />
    <Box sx={{
      position: 'absolute', width: 450, height: 450, bottom: -120, left: -100,
      background: `radial-gradient(circle, ${alpha('#00a882', 0.14)} 0%, transparent 70%)`,
      borderRadius: '50%',
      animation: 'blobFloat2 10s ease-in-out infinite',
      '@keyframes blobFloat2': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(20px, -20px) scale(1.08)' },
      },
    }} />
    <Box sx={{
      position: 'absolute', inset: 0,
      backgroundImage: `
        linear-gradient(${alpha(PRIMARY, 0.04)} 1px, transparent 1px),
        linear-gradient(90deg, ${alpha(PRIMARY, 0.04)} 1px, transparent 1px)
      `,
      backgroundSize: '48px 48px',
    }} />
  </Box>
);

const StatPill = ({ icon, label, value, delay }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5,
    background: 'rgba(255,255,255,0.9)',
    border: `1px solid ${alpha(PRIMARY, 0.15)}`,
    borderRadius: 3, px: 2, py: 1.25,
    boxShadow: `0 4px 20px ${alpha(PRIMARY, 0.12)}`,
    animation: `pillFadeIn 0.6s ease ${delay}s both`,
    '@keyframes pillFadeIn': {
      from: { opacity: 0, transform: 'translateY(12px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{ fontSize: 20 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>{value}</Typography>
    </Box>
  </Box>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email address';
    if (!password) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      // login() returns the full user object including role.isSuperAdmin
      const u = await login({ email: email.trim(), password });

      // Route based on role — check the returned user directly, not context state
      // (context state may not have updated yet at this point in the render cycle)
      if (u?.role?.isSuperAdmin) {
        navigate('/superadmin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Login failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    if (field === 'email') setEmail(value);
    else setPassword(value);
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: '' }));
    if (error) setError('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(145deg, #e8f2fb 0%, #f0f4f8 50%, #e8f5f1 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', p: 2,
    }}>
      <BlobBg />

      <Box sx={{
        position: 'relative', zIndex: 1,
        display: 'flex', width: '100%', maxWidth: 960, minHeight: 560,
        borderRadius: 4, overflow: 'hidden',
        boxShadow: `0 24px 80px ${alpha(PRIMARY, 0.18)}, 0 4px 20px rgba(0,0,0,0.08)`,
        border: `1px solid ${alpha(PRIMARY, 0.12)}`,
        animation: 'cardEntrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        '@keyframes cardEntrance': {
          from: { opacity: 0, transform: 'scale(0.94) translateY(20px)' },
          to:   { opacity: 1, transform: 'scale(1) translateY(0)' },
        },
      }}>

        {/* Left panel */}
        <Box sx={{
          flex: '0 0 42%', display: { xs: 'none', md: 'flex' },
          flexDirection: 'column', justifyContent: 'space-between',
          p: 5,
          background: `linear-gradient(155deg, #0a5a8f 0%, ${PRIMARY} 45%, #1a8fd6 100%)`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', width: 320, height: 320, top: -80, right: -80, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', width: 200, height: 200, top: -30, right: -30, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', width: 250, height: 250, bottom: -60, left: -60, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%' }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5,
              background: alpha('#fff', 0.12), border: `1px solid ${alpha('#fff', 0.2)}`,
              borderRadius: 2.5, px: 2, py: 1, mb: 4,
            }}>
              <AutoAwesome sx={{ color: '#fff', fontSize: 18 }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                GoVirtual
              </Typography>
            </Box> */}
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.15, fontSize: { md: '1.9rem', lg: '2.2rem' }, mb: 1.5 }}>
              Virtual<br />Events<br /> Platform
            </Typography>
            {/* <Typography sx={{ color: alpha('#fff', 0.75), fontSize: '0.95rem', lineHeight: 1.6 }}>
              Your all-in-one platform for virtual & hybrid event management.
            </Typography> */}
          </Box>

          {/* <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <StatPill icon="🎪" label="Total Events"     value="Managed"    delay={0.2} />
            <StatPill icon="👥" label="Participants"      value="Registered" delay={0.35} />
            <StatPill icon="📋" label="Form Submissions" value="Collected"  delay={0.5} />
          </Box> */}
        </Box>

        {/* Right panel */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: { xs: 3, sm: 5, md: 6 }, background: '#fff' }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mb: 3 }}>
              <AutoAwesome sx={{ color: PRIMARY, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 800, color: PRIMARY, letterSpacing: '0.05em', fontSize: '1rem' }}>GoVirtual</Typography>
            </Box>
            {/* <Box sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 52, height: 52, borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.12)}, ${alpha(PRIMARY, 0.06)})`,
              border: `1px solid ${alpha(PRIMARY, 0.15)}`, mb: 2.5,
            }}>
              <AdminPanelSettingsOutlined sx={{ color: PRIMARY, fontSize: 26 }} />
            </Box> */}
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5, letterSpacing: '-0.01em' }}>
              Admin Sign In
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sign in to access your organization's dashboard
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{
              mb: 3, borderRadius: 2, fontSize: '0.875rem',
              animation: 'shakeAlert 0.4s ease',
              '@keyframes shakeAlert': {
                '0%, 100%': { transform: 'translateX(0)' },
                '20%': { transform: 'translateX(-6px)' },
                '40%': { transform: 'translateX(6px)' },
                '60%': { transform: 'translateX(-4px)' },
                '80%': { transform: 'translateX(4px)' },
              },
            }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Email Address" type="email"
                value={email} onChange={e => handleFieldChange('email', e.target.value)}
                error={!!fieldErrors.email} helperText={fieldErrors.email}
                fullWidth autoComplete="email" autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: fieldErrors.email ? 'error.main' : alpha(PRIMARY, 0.5), fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiFormHelperText-root': { mx: 0, mt: 0.5 } }}
              />
              <TextField
                label="Password" type={showPass ? 'text' : 'password'}
                value={password} onChange={e => handleFieldChange('password', e.target.value)}
                error={!!fieldErrors.password} helperText={fieldErrors.password}
                fullWidth autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: fieldErrors.password ? 'error.main' : alpha(PRIMARY, 0.5), fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass(v => !v)} edge="end" size="small" tabIndex={-1}>
                        {showPass
                          ? <VisibilityOff sx={{ fontSize: 18, color: 'text.secondary' }} />
                          : <Visibility    sx={{ fontSize: 18, color: 'text.secondary' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiFormHelperText-root': { mx: 0, mt: 0.5 } }}
              />
            </Box>

            <Button
              type="submit" variant="contained" fullWidth disabled={loading}
              sx={{
                mt: 3.5, py: 1.4, fontSize: '1rem', fontWeight: 700, letterSpacing: '0.01em',
                position: 'relative', overflow: 'hidden',
                '&::after': {
                  content: '""', position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                  transform: 'translateX(-100%)', transition: 'transform 0.5s ease',
                },
                '&:not(:disabled):hover::after': { transform: 'translateX(100%)' },
              }}
            >
              {loading
                ? <CircularProgress size={22} thickness={3} sx={{ color: 'rgba(255,255,255,0.8)' }} />
                : 'Sign In to Dashboard'
              }
            </Button>
          </Box>

          {/* <Divider sx={{ my: 3 }}>
            <Chip label="Secure Access" size="small" sx={{ fontSize: '0.7rem', color: 'text.secondary', border: `1px solid ${alpha(PRIMARY, 0.15)}`, background: alpha(PRIMARY, 0.04) }} />
          </Divider> */}

          {/* <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.6 }}>
            Access is restricted to authorized administrators only.{' '}
            <Box component="span" sx={{ color: PRIMARY, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              Contact your organization admin
            </Box>{' '}
            if you need access.
          </Typography> */}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ position: 'fixed', bottom: 16, right: 20, color: alpha('#4a6a85', 0.6), fontSize: '0.7rem', zIndex: 10 }}>
        GV Events Admin v1.0
      </Typography>
    </Box>
  );
}